import type {Suggestion, Suggestions} from '@github-ui/inline-autocomplete/types'
import {RepoIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useMemo, useState} from 'react'

import {useRepositoryItems} from '../../hooks/use-repository-items'
import type {TopicItem} from '../../utils/copilot-chat-types'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '../../utils/CopilotChatContext'
import {ReferenceMention} from '../../utils/reference-mention'
import {createEmptyStateSuggestion} from './EmptyStateSuggestion'
import {RepositorySuggestion} from './RepositorySuggestion'
import {SSOPromptSuggestion} from './SSOOrgSuggestion'
import {useThrottledFilterQuery} from './use-throttled-filter-query'
import {ssoUrl} from './useSSOOrgsSuggestions'

export interface RepositoriesQuery {
  category: 'repositories'
  nextCategory?: 'issues' | 'pulls' | 'discussions' | 'files'
  filter: string
  /**
   * When `true`, this query was triggered proactively (eg the user typed
   * `owner/repo` without `@`) rather than by the user explicitly opening the
   * picker. We hide the SSO prompt in this case because we never want to show
   * the overlay just because the user has SSO orgs configured. The overlay
   * should only appear when there are real repo matches.
   */
  proactive?: boolean
}

const MAX_SEEN_REPOSITORIES = 100

/**
 * Sorts repos in the set of selected repo IDs to the top of the list.
 */
const sortSelectedToTop = (selectedRepoIds: Set<number | string>) => (a: TopicItem, b: TopicItem) => {
  if (selectedRepoIds.has(a.databaseId) && !selectedRepoIds.has(b.databaseId)) return -1
  if (selectedRepoIds.has(b.databaseId) && !selectedRepoIds.has(a.databaseId)) return 1
  return 0
}

export function useRepositoriesSuggestions(query: RepositoriesQuery | null) {
  const {currentReferences, topRepositoriesCache, ssoOrganizations} = useChatState()

  const activeQuery = useThrottledFilterQuery(query)

  const {repositories, loading} = useRepositoryItems(activeQuery?.filter ?? '', activeQuery !== null)

  const selectedRepoIds = useMemo(
    () => new Set(currentReferences.filter(r => r.type === 'repository').map(r => r.id)),
    [currentReferences],
  )

  // We keep two lists of repos to filter against:
  //
  //   1. topRepositoriesCache – the default repos shown when the menu opens.
  //   2. seenRepositories – every repo the server has sent back during this
  //      session (newest first, max 100).
  //
  // We hold on to past results because the server can return fewer (or zero)
  // repos for a longer query. Without this, a repo could vanish from the
  // list even though the user's text still matches it.
  const [seenRepositories, setSeenRepositories] = useState<TopicItem[]>([])
  const [trackedRepositories, setTrackedRepositories] = useState<TopicItem[]>([])
  if (repositories !== trackedRepositories) {
    setTrackedRepositories(repositories)
    if (repositories.length > 0) {
      setSeenRepositories(prev => {
        const incomingNwos = new Set(repositories.map(r => r.nwo))
        const previousMinusDuplicates = prev.filter(r => !incomingNwos.has(r.nwo))
        return [...repositories, ...previousMinusDuplicates].slice(0, MAX_SEEN_REPOSITORIES)
      })
    }
  }

  const searchPool = useMemo(() => {
    const seen = new Set<string>()
    const merged: TopicItem[] = []
    for (const repo of [...seenRepositories, ...(topRepositoriesCache ?? [])]) {
      if (seen.has(repo.nwo)) continue
      seen.add(repo.nwo)
      merged.push(repo)
    }
    return merged
  }, [seenRepositories, topRepositoriesCache])

  // Filter the list locally as the user types. This only re-runs when they
  // press a key or when new server results come in.
  const liveFilter = (query?.filter ?? '').toLowerCase()
  const filteredRepos = useMemo(() => {
    if (!liveFilter) return searchPool
    return searchPool.filter(r => r.nwo.toLowerCase().includes(liveFilter))
  }, [searchPool, liveFilter])

  const suggestions = useMemo<Suggestions | null>(() => {
    if (!activeQuery) return null

    // Proactive triggers (eg `github/a`) opt out of showing the loading skeleton or an
    // empty list. The user did not explicitly ask for a picker, so we keep the overlay
    // hidden until there is something concrete to show. `InlineAutocomplete` already
    // hides the overlay when suggestions is `null`.
    if (loading === 'initial') return activeQuery.proactive ? null : 'loading'

    const isMultistep = activeQuery.nextCategory !== undefined

    const results = filteredRepos
      .toSorted(sortSelectedToTop(selectedRepoIds))
      .slice(0, 5)
      .map<Suggestion>(repo => ({
        value: isMultistep ? null : ReferenceMention.stringify({type: 'repository', repo: repo.nwo}),
        key: repo.nwo,
        render: props => <RepositorySuggestion repository={repo} isMultistep={isMultistep} {...props} />,
      }))

    if (ssoOrganizations.length > 0 && !activeQuery.proactive)
      results.push({
        value: null,
        key: ssoOrganizations.length === 1 ? `open-link:${ssoUrl(ssoOrganizations[0]!)}` : 'sso-orgs',
        render: props => (
          <>
            <ActionList.Divider />
            <SSOPromptSuggestion orgs={ssoOrganizations} {...props} />
          </>
        ),
      })

    if (activeQuery.proactive && results.length === 0) return null

    // When the picker has settled with no results and no SSO prompt to surface,
    // show a non-selectable empty-state item so the menu stays visible instead
    // of silently closing — which looks to the user like the click did nothing.
    // This is especially important for users (eg Copilot Free) whose accessible
    // repository list comes back empty.
    if (results.length === 0 && loading === false) {
      return [createEmptyStateSuggestion('repositories', <RepoIcon />, 'No repositories found')]
    }

    return results
  }, [filteredRepos, loading, activeQuery, selectedRepoIds, ssoOrganizations])

  return {suggestions, stale: loading === true}
}
